import type { TransferAssetOwnershipInput } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../lib/use-toast';
import { listTruckOwnershipPeriods, transferTruckOwnership, type TruckOwnershipPeriod } from './trucks.api';

export type TruckOwnershipState = { status: 'loading' } | { status: 'loaded'; periods: TruckOwnershipPeriod[] } | { status: 'forbidden' } | { status: 'error' };

function toErrorState(err: unknown): TruckOwnershipState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The Ownership tab's periods and its one mutation, transfer — reloaded after a successful transfer, same shape as `useTruckDocuments`. */
export function useTruckOwnership(truckId: string, truckRegistration: string) {
  const [state, setState] = useState<TruckOwnershipState>({ status: 'loading' });
  const [transferring, setTransferring] = useState(false);
  const toast = useToast();

  async function load() {
    try {
      setState({ status: 'loaded', periods: await listTruckOwnershipPeriods(truckId) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  useEffect(() => {
    let cancelled = false;
    listTruckOwnershipPeriods(truckId)
      .then((periods) => {
        if (!cancelled) setState({ status: 'loaded', periods });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [truckId]);

  async function transfer(input: TransferAssetOwnershipInput, ownerName: string): Promise<boolean> {
    setTransferring(true);
    try {
      await transferTruckOwnership(truckId, input);
      await load();
      toast.success(`Truck ${truckRegistration} ownership transferred to ${ownerName}`);
      return true;
    } catch (err) {
      toast.error(err instanceof ApiError && err.status === 409 ? 'The new period must start after the current one began' : "Couldn't transfer ownership");
      return false;
    } finally {
      setTransferring(false);
    }
  }

  return { state, transferring, transfer };
}
